import { useEffect, useState, useMemo, useCallback } from "react";
import { orderBy } from "lodash";
import { PropertyCard } from "../../components/property-card";
import { Skeleton } from "../../components/ui/skeleton";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MapPin, TrendingUp, Home, DollarSign, Filter, Map as MapIcon } from "lucide-react";
import { formatCurrency } from "../../utils/property-formatters";
import NearbyPropertiesMap from "../../components/NearbyPropertiesMap";
import { useNearbyProperties } from "../../hooks";
import { getBuildingSquareFeet } from "../../utils/property-calculations";

/**
 * Haversine distance between two lat/lng points.
 * @returns {number} Distance in miles
 */
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const NearbyInsightsEnhanced = ({
  property = {},
  propertyId,
  setMarketValuePerSqft = () => {},
  /** When true (e.g. property opened with deal-finder ?dealCalc=), do not overwrite $/sqft from nearby comps */
  skipAutoMarketValuePerSqft = false,
}) => {
  // Use custom hook for fetching properties from both APIs (comparables + search)
  const { 
    properties: nearbyInsights, 
    loading: isLoadingNearbyInsights,
    error 
  } = useNearbyProperties(propertyId, property);

  const [filteredInsights, setFilteredInsights] = useState([]);
  const [sortBy, setSortBy] = useState("closest");
  const [filterStatus, setFilterStatus] = useState("all");

  // Compute distance for a property relative to the current property
  const getDistance = useCallback(
    (p) => {
      if (!property?.latitude || !property?.longitude || !p.latitude || !p.longitude) {
        return Infinity;
      }
      return haversineDistance(property.latitude, property.longitude, p.latitude, p.longitude);
    },
    [property?.latitude, property?.longitude]
  );
  const [showMap, setShowMap] = useState(true);

  // Calculate statistics from properties using useMemo
  const statistics = useMemo(() => {
    if (!nearbyInsights || nearbyInsights.length === 0) {
      return {
        avgValue: 0,
        avgPricePerSqft: 0,
        totalProperties: 0,
        avgBedrooms: 0,
      };
    }

    let totalValue = 0;
    let totalPricePerSqft = 0;
    let totalBedrooms = 0;
    let validPricePerSqftCount = 0;
    let validBedroomsCount = 0;

    nearbyInsights.forEach((prop) => {
      totalValue += prop.estimatedValue || 0;

      const sqft = getBuildingSquareFeet(prop);
      if (prop.estimatedValue && sqft) {
        totalPricePerSqft += prop.estimatedValue / sqft;
        validPricePerSqftCount++;
      }

      if (prop.bedrooms) {
        totalBedrooms += prop.bedrooms;
        validBedroomsCount++;
      }
    });

    return {
      avgValue: Math.ceil(totalValue / nearbyInsights.length),
      avgPricePerSqft:
        validPricePerSqftCount > 0
          ? Math.ceil(totalPricePerSqft / validPricePerSqftCount)
          : 0,
      totalProperties: nearbyInsights.length,
      avgBedrooms:
        validBedroomsCount > 0
          ? (totalBedrooms / validBedroomsCount).toFixed(1)
          : 0,
    };
  }, [nearbyInsights]);

  // Apply sorting and filtering
  useEffect(() => {
    let filtered = [...nearbyInsights];

    // Exclude properties with no estimated value (shows as "N/A" total cost)
    filtered = filtered.filter((p) => p.estimatedValue && p.estimatedValue > 0);

    // Apply status filter
    if (filterStatus === "onMarket") {
      filtered = filtered.filter((p) => p.onMarket);
    } else if (filterStatus === "offMarket") {
      filtered = filtered.filter((p) => !p.onMarket);
    }

    // Apply sorting
    switch (sortBy) {
      case "closest":
        filtered = filtered.sort((a, b) => getDistance(a) - getDistance(b));
        break;
      case "value-desc":
        filtered = orderBy(filtered, ["estimatedValue"], ["desc"]);
        break;
      case "value-asc":
        filtered = orderBy(filtered, ["estimatedValue"], ["asc"]);
        break;
      case "sqft-desc":
        filtered = orderBy(filtered, [(p) => getBuildingSquareFeet(p)], ["desc"]);
        break;
      case "sqft-asc":
        filtered = orderBy(filtered, [(p) => getBuildingSquareFeet(p)], ["asc"]);
        break;
      default:
        break;
    }

    setFilteredInsights(filtered);
  }, [nearbyInsights, sortBy, filterStatus, getDistance]);

  // Calculate market value per sqft for top 10% of properties
  useEffect(() => {
    if (skipAutoMarketValuePerSqft) return;
    if (!nearbyInsights || nearbyInsights.length === 0) return;

    let totalProperties = 0;
    let totalPropertiesValue = 0;
    let totalPropertiesSqFt = 0;

    // Take top 10% of properties
    const topTenPercent = Math.ceil(nearbyInsights.length / 10);
    
    nearbyInsights.slice(0, topTenPercent).forEach((property) => {
      const { estimatedValue = 0 } = property;
      const sqft = getBuildingSquareFeet(property);
      totalProperties += 1;
      totalPropertiesValue += estimatedValue || 0;
      totalPropertiesSqFt += sqft || 0;
    });

    if (totalProperties > 0 && totalPropertiesSqFt > 0) {
      setMarketValuePerSqft(
        Math.ceil(1.3 * totalPropertiesValue / totalPropertiesSqFt)
      );
    }
  }, [nearbyInsights, setMarketValuePerSqft, skipAutoMarketValuePerSqft]);


  if (isLoadingNearbyInsights) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Skeleton className="w-48 sm:w-72 h-8 sm:h-10" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-20 sm:h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {[...Array(8)].map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <Skeleton className="h-[140px] sm:h-[180px] w-full" />
              <div className="p-2 sm:p-3 space-y-2">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
              Similar Properties Nearby
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Discover comparable properties in the area
            </p>
          </div>
        </div>
      </div>

      {/* Map View Toggle */}
      {nearbyInsights.length > 0 && (
        <div className="flex items-center justify-between">
          <Button
            variant={showMap ? "default" : "outline"}
            size="sm"
            onClick={() => setShowMap(!showMap)}
            className="gap-2"
          >
            <MapIcon className="h-4 w-4" />
            {showMap ? "Hide Map" : "Show Map"}
          </Button>
        </div>
      )}

      {/* Interactive Map - Shows all properties regardless of filters */}
      {showMap && nearbyInsights.length > 0 && (
        <NearbyPropertiesMap
          properties={nearbyInsights}
          centerProperty={property}
          height="500px"
        />
      )}

      {/* Statistics Cards */}
      {nearbyInsights.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Total Properties
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-primary">
                    {statistics.totalProperties}
                  </p>
                </div>
                <Home className="h-6 w-6 sm:h-8 sm:w-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Avg. Value
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-green-600">
                    {formatCurrency(statistics.avgValue)}
                  </p>
                </div>
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-600/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Avg. $/sq ft
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-blue-600">
                    {formatCurrency(statistics.avgPricePerSqft)}
                  </p>
                </div>
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Avg. Bedrooms
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-purple-600">
                    {statistics.avgBedrooms}
                  </p>
                </div>
                <Home className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Sorting */}
      {nearbyInsights.length > 0 && (
        <Card className="shadow-md">
          <CardContent className="pt-4 sm:pt-6 pb-4">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex-1">
                  <label className="text-xs sm:text-sm font-medium mb-2 flex items-center gap-2">
                    <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
                    Sort By
                  </label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="closest">
                        Closest Home First
                      </SelectItem>
                      <SelectItem value="value-desc">
                        Price: High to Low
                      </SelectItem>
                      <SelectItem value="value-asc">
                        Price: Low to High
                      </SelectItem>
                      <SelectItem value="sqft-desc">
                        Size: Largest First
                      </SelectItem>
                      <SelectItem value="sqft-asc">
                        Size: Smallest First
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <label className="text-xs sm:text-sm font-medium mb-2 flex items-center gap-2">
                    <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
                    Filter Status
                  </label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Properties</SelectItem>
                      <SelectItem value="onMarket">On Market Only</SelectItem>
                      <SelectItem value="offMarket">Off Market Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <Badge variant="secondary" className="text-xs sm:text-sm w-fit">
                  Showing {filteredInsights.length} of {nearbyInsights.length}{" "}
                  properties
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setSortBy("closest");
                    setFilterStatus("all");
                  }}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Properties Grid */}
      {filteredInsights && filteredInsights.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {filteredInsights.map((property) => {
            return (
              <PropertyCard
                imageHeight={180}
                key={property.id}
                property={property}
                showImageModalOnClick={true}
                showPrices={true}
                target={"_blank"}
              />
            );
          })}
        </div>
      ) : nearbyInsights.length > 0 ? (
        <Card className="p-6 sm:p-8">
          <div className="text-center">
            <Filter className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-xs sm:text-sm text-muted-foreground">
              No properties match your current filters
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 sm:mt-4"
              onClick={() => {
                setSortBy("closest");
                setFilterStatus("all");
              }}
            >
              Reset Filters
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-6 sm:p-8">
          <div className="text-center">
            <MapPin className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-base sm:text-lg font-semibold mb-2">
              No Nearby Properties Found
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground">
              We couldn't find similar properties in this area at the moment.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};
